/**
 * Detects the well-known Sentry noise pattern where a Promise is rejected
 * with a non-`Error` value (typically a raw DOM `Event`/`CustomEvent`, or a
 * plain object with no keys) instead of an `Error`. This is almost always
 * caused by third-party scripts or browser extensions — e.g. an analytics/ad
 * script that listens for `unhandledrejection` and re-dispatches the whole
 * event, or a fetch/XHR polyfill that rejects with the underlying error
 * `Event` on failure.
 *
 * These rejections never carry a usable stack trace (JS never had one to
 * attach, regardless of origin), so they are not actionable on their own.
 * We still want visibility if the *volume* of this pattern spikes (which
 * would suggest a real regression in our own code), so callers should
 * downgrade + group these in Sentry rather than dropping them outright.
 */
export function isNonErrorPromiseRejection(exception: unknown): boolean {
  if (exception === null || typeof exception !== 'object') {
    return false;
  }
  if (exception instanceof Error) {
    return false;
  }
  if (typeof Event !== 'undefined' && exception instanceof Event) {
    return true;
  }

  return Object.keys(exception).length === 0;
}
